import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useAuth } from './AuthContext';
import { Moon, Sun, HelpCircle, FileText, LogOut, Users, UserPlus, UserMinus, Loader2, CheckCircle, Clock } from 'lucide-react';
import { toast } from "sonner";
import axios from 'axios';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Settings = () => {
  const { user, token, theme, toggleTheme, logout } = useAuth();
  const [dependentEmail, setDependentEmail] = useState('');
  const [loadingDependent, setLoadingDependent] = useState(false);
  const [dependentStatus, setDependentStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    if (token) {
      loadDependentStatus();
    }
  }, [token]);

  const loadDependentStatus = async () => {
    try {
      const response = await axios.get(`${API}/dependents/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDependentStatus(response.data);
    } catch (error) {
      console.error('Error loading dependent status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleAddDependent = async () => {
    if (!dependentEmail.trim()) {
      toast.error('Digite o email do dependente');
      return;
    }

    setLoadingDependent(true);
    try {
      const response = await axios.post(
        `${API}/dependents/add`,
        { dependent_email: dependentEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setDependentEmail('');
      loadDependentStatus();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao adicionar dependente';
      toast.error(errorMsg);
    } finally {
      setLoadingDependent(false);
    }
  };

  const handleRemoveDependent = async () => {
    if (!dependentStatus?.dependent_email) return;

    setLoadingDependent(true);
    try {
      const response = await axios.post(
        `${API}/dependents/remove`,
        { dependent_email: dependentStatus.dependent_email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      loadDependentStatus();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao remover dependente';
      toast.error(errorMsg);
    } finally {
      setLoadingDependent(false);
    }
  };

  return (
    <div className="settings-container">
      <Card className="settings-card">
        <CardHeader>
          <CardTitle>Configurações</CardTitle>
          <CardDescription>
            Gerencie suas preferências e informações
          </CardDescription>
        </CardHeader>
        <CardContent className="settings-content">
          {/* User Info */}
          <div className="settings-section">
            <h3>Conta</h3>
            <div className="user-info">
              <p className="user-email">{user?.email}</p>
              {user?.name && <p className="user-name">{user.name}</p>}
            </div>
            <Button 
              variant="outline" 
              onClick={logout} 
              className="logout-btn"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>

          <Separator className="my-6" />

          {/* Theme Toggle */}
          <div className="settings-section">
            <div className="theme-toggle-container">
              <div className="theme-info">
                <h3>Tema</h3>
                <p className="theme-desc">
                  {theme === 'dark' ? 'Modo Escuro' : 'Modo Claro'}
                </p>
              </div>
              <div className="theme-switch">
                {theme === 'dark' ? (
                  <Moon className="w-5 h-5 theme-icon" />
                ) : (
                  <Sun className="w-5 h-5 theme-icon" />
                )}
                <Switch 
                  checked={theme === 'dark'} 
                  onCheckedChange={toggleTheme}
                  data-testid="theme-toggle"
                />
              </div>
            </div>
          </div>

          <Separator className="my-6" />

          {/* Dependent Management - Only for Empresarial Plan */}
          {user?.plan === 'empresarial' && (
            <>
              <div className="settings-section">
                <div className="section-header">
                  <Users className="w-5 h-5" />
                  <h3>Gerenciar Dependente</h3>
                </div>
                <p className="section-desc" style={{ marginBottom: '1rem', color: '#888', fontSize: '0.9rem' }}>
                  Seu plano Empresarial inclui acesso para 1 dependente adicional.
                </p>

                {loadingStatus ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#888' }}>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Carregando...</span>
                  </div>
                ) : dependentStatus?.is_dependent ? (
                  // User is a dependent
                  <div style={{ 
                    background: 'rgba(139, 92, 246, 0.1)', 
                    borderRadius: '8px', 
                    padding: '1rem',
                    border: '1px solid rgba(139, 92, 246, 0.3)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <CheckCircle className="w-4 h-4" style={{ color: '#8b5cf6' }} />
                      <span style={{ fontWeight: '600' }}>Você é um dependente</span>
                    </div>
                    <p style={{ color: '#888', fontSize: '0.85rem' }}>
                      Conta principal: {dependentStatus.parent_email}
                    </p>
                  </div>
                ) : dependentStatus?.has_dependent ? (
                  // User has a dependent
                  <div>
                    <div style={{ 
                      background: 'rgba(34, 197, 94, 0.1)', 
                      borderRadius: '8px', 
                      padding: '1rem',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        {dependentStatus.dependent_info?.is_active ? (
                          <CheckCircle className="w-4 h-4" style={{ color: '#22c55e' }} />
                        ) : (
                          <Clock className="w-4 h-4" style={{ color: '#f59e0b' }} />
                        )}
                        <span style={{ fontWeight: '600' }}>
                          {dependentStatus.dependent_info?.is_active ? 'Dependente Ativo' : 'Convite Pendente'}
                        </span>
                      </div>
                      <p style={{ color: '#888', fontSize: '0.85rem' }}>
                        {dependentStatus.dependent_email}
                      </p>
                      {dependentStatus.dependent_info?.name && (
                        <p style={{ color: '#aaa', fontSize: '0.8rem' }}>
                          {dependentStatus.dependent_info.name}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleRemoveDependent}
                      disabled={loadingDependent}
                      style={{ borderColor: '#ef4444', color: '#ef4444' }}
                    >
                      {loadingDependent ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <UserMinus className="w-4 h-4 mr-2" />
                      )}
                      Remover Dependente
                    </Button>
                  </div>
                ) : (
                  // User can add a dependent
                  <div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Input
                        type="email"
                        value={dependentEmail}
                        onChange={(e) => setDependentEmail(e.target.value)}
                        placeholder="email@dependente.com"
                        style={{ flex: 1 }}
                        data-testid="dependent-email-input"
                      />
                      <Button
                        onClick={handleAddDependent}
                        disabled={loadingDependent || !dependentEmail.trim()}
                        data-testid="add-dependent-btn"
                      >
                        {loadingDependent ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <UserPlus className="w-4 h-4 mr-2" />
                        )}
                        Adicionar
                      </Button>
                    </div>
                    <p style={{ color: '#888', fontSize: '0.75rem' }}>
                      O dependente receberá acesso ao plano Empresarial ao criar uma conta com este email.
                    </p>
                  </div>
                )}
              </div>

              <Separator className="my-6" />
            </>
          )}

          {/* FAQ */}
          <div className="settings-section">
            <div className="section-header">
              <HelpCircle className="w-5 h-5" />
              <h3>Perguntas Frequentes</h3>
            </div>
            <Accordion type="single" collapsible className="faq-accordion">
              <AccordionItem value="item-1">
                <AccordionTrigger>Como funciona a análise de risco?</AccordionTrigger>
                <AccordionContent>
                  A análise de risco avalia diversos fatores do seu negócio (margem, endividamento, separação PJ/PF) 
                  e gera um score de 0-100, indicando o nível de risco atual.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger>O que é o Modo Crise?</AccordionTrigger>
                <AccordionContent>
                  O Modo Crise é ativado automaticamente quando seu negócio atinge um score de risco alto (≥70). 
                  Ele fornece ações diretas e urgentes para estabilizar a situação.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger>Como a IA me ajuda?</AccordionTrigger>
                <AccordionContent>
                  A IA analisa seu contexto específico e fornece educação financeira personalizada, 
                  exemplos adaptados ao seu tipo de negócio e recomendações práticas.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4">
                <AccordionTrigger>Meus dados estão seguros?</AccordionTrigger>
                <AccordionContent>
                  Sim! Todos os dados são criptografados e armazenados com segurança. 
                  Suas informações financeiras são privadas e vinculadas apenas à sua conta.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <Separator className="my-6" />

          {/* Terms */}
          <div className="settings-section">
            <div className="section-header">
              <FileText className="w-5 h-5" />
              <h3>Termos de Uso</h3>
            </div>
            <div className="terms-content">
              <p>
                <strong>AtlasIA - Consultoria Empresarial Inteligente</strong>
              </p>
              <p className="terms-text">
                Ao usar o AtlasIA, você concorda em utilizar a plataforma apenas para fins de consultoria empresarial. 
                As análises e recomendações fornecidas são baseadas em inteligência artificial e devem ser consideradas 
                como orientações gerais. Consulte sempre um profissional certificado para decisões críticas.
              </p>
              <p className="terms-text">
                <strong>Privacidade:</strong> Suas informações são protegidas e não compartilhadas com terceiros.
              </p>
              <p className="terms-text">
                <strong>Uso Responsável:</strong> As análises são ferramentas de apoio à decisão, não substituem consultoria profissional.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
